import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChartLineUpIcon,
  PencilSimpleLineIcon,
} from '@phosphor-icons/react';
import { format, parseISO } from 'date-fns';
import {
  Area,
  Bar,
  ComposedChart,
  Curve,
  ReferenceLine,
  ResponsiveContainer,
  usePlotArea,
  useXAxisScale,
  useYAxisScale,
  XAxis,
  YAxis,
  ZIndexLayer,
} from 'recharts';

import { cn } from '@shared/lib/utils';
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from '@shared/ui';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@shared/ui/card';
import { DropdownMenuGroup, DropdownMenuItem } from '@shared/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@shared/ui/tooltip';

import { CardActionMenu } from './CardActionMenu';
import {
  CardMetric,
  CardMetricEmphasis,
  CardMetricPrimary,
  CardMetricSecondary,
  CardMetricValue,
} from './CardMetric';

const chartConfig: ChartConfig = {
  score: {
    label: 'Score',
    color: 'var(--color-blue-500)',
  },
  trend: {
    label: 'Trend',
    color: 'var(--color-blue-500)',
  },
};

const MAX_VISIBLE_POINTS = 30;
const SCORE_MAX = 805;
const SCORE_MIN = 205;
const SCORE_BAR_SIZE = 12;
const TREND_WINDOW_SIZE = 5;
const AXIS_GUTTER_WIDTH = 52;
const AXIS_COLUMN_OFFSET = 4;
const CHART_RIGHT_MARGIN = 8;
const BADGE_Z_INDEX = 3000;
const BADGE_HEIGHT = 20;
const BADGE_PADDING_X = 8;
const BADGE_FONT_SIZE = 12;
const AXIS_TICK_FONT_SIZE = 10;
const CHART_TOOLTIP_ANIMATION_DURATION = 120;
const CHANGE_TOOLTIP_DELAY = 300;

export interface ScoreCardChartDatum {
  date: string;
  score: number;
}

type ScoreTrendChartDatum = Omit<ScoreCardChartDatum, 'score'> & {
  score: number | null;
  trend: number | null;
};

interface ScoreComparisonPoint {
  date: string;
  value: number;
}

export interface ScoreCardProps {
  className?: string;
  chartData: ScoreCardChartDatum[];
  targetScore: number;
}

interface AxisTickRendererProps {
  x?: number | string;
  y?: number | string;
  payload?: {
    value: number | string;
  };
}

const isChartCoordinate = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const getBadgeWidth = (value: string) => Math.max(40, value.length * 8 + BADGE_PADDING_X * 2);
const getAxisColumnX = (plotAreaX: number, plotAreaWidth: number) =>
  plotAreaX + plotAreaWidth + AXIS_COLUMN_OFFSET;
const getTooltipMetricLabel = (name: unknown) => {
  const key = typeof name === 'string' || typeof name === 'number' ? String(name) : 'value';
  return chartConfig[key]?.label ?? key;
};
const formatTooltipMetricValue = (value: unknown) => {
  if (value == null) {
    return 'N/A';
  }

  return typeof value === 'number' ? value.toLocaleString() : String(value);
};

const renderValueBadge = ({
  x,
  y,
  value,
  fill,
  stroke,
  textFill,
}: {
  x: number;
  y: number;
  value: number | string;
  fill: string;
  stroke: string;
  textFill: string;
}) => {
  const label = String(value);
  const width = getBadgeWidth(label);

  return (
    <g>
      <rect x={x} y={y} width={width} height={BADGE_HEIGHT} rx={6} fill={fill} stroke={stroke} />
      <text
        x={x + width / 2}
        y={y + BADGE_HEIGHT / 2 + 4}
        textAnchor="middle"
        fill={textFill}
        fontSize={BADGE_FONT_SIZE}
        fontWeight={600}
      >
        {label}
      </text>
    </g>
  );
};

const renderAxisTick = ({ x, y, payload }: AxisTickRendererProps) => {
  if (typeof x !== 'number' || typeof y !== 'number' || payload?.value == null) {
    return null;
  }

  return (
    <text
      x={x + AXIS_COLUMN_OFFSET}
      y={y}
      dy={4}
      textAnchor="start"
      fill="var(--color-gray-500)"
      fontSize={AXIS_TICK_FONT_SIZE}
      fontWeight={500}
    >
      {payload.value}
    </text>
  );
};

const ChartSideBadge = ({
  value,
  yValue,
  fill,
  stroke,
  textFill,
}: {
  value: number | string;
  yValue: number;
  fill: string;
  stroke: string;
  textFill: string;
}) => {
  const plotArea = usePlotArea();
  const yScale = useYAxisScale();

  if (!plotArea || !yScale) {
    return null;
  }

  const y = yScale(yValue);

  if (!isChartCoordinate(y)) {
    return null;
  }

  return renderValueBadge({
    x: getAxisColumnX(plotArea.x, plotArea.width),
    y: Math.max(4, y - BADGE_HEIGHT / 2),
    value,
    fill,
    stroke,
    textFill,
  });
};

const clampScore = (score: number) => {
  if (score < SCORE_MIN) {
    return null;
  }

  return Math.min(score, SCORE_MAX);
};

type ScoreChangeDirection = 'up' | 'down' | 'flat';

const getChangeDirection = (changePercent: number): ScoreChangeDirection => {
  if (changePercent > 0) {
    return 'up';
  }

  if (changePercent < 0) {
    return 'down';
  }

  return 'flat';
};

const ScoreChangeIndicator = ({
  changePercent,
  startPoint,
  endPoint,
}: {
  changePercent: number;
  startPoint: ScoreComparisonPoint | null;
  endPoint: ScoreComparisonPoint | null;
}) => {
  const direction = getChangeDirection(changePercent);
  const absoluteChangePercent = Math.abs(changePercent);
  const content = (
    <div className="flex flex-col gap-1">
      <div className="text-xs font-medium uppercase tracking-wide text-background/70">
        Compared Values
      </div>
      <div className="flex items-center justify-between gap-4">
        <span className="text-background/70">Start</span>
        <span className="tabular-nums">
          {startPoint
            ? `${format(parseISO(startPoint.date), 'MMM d')}: ${String(startPoint.value)}`
            : 'N/A'}
        </span>
      </div>
      <div className="flex items-center justify-between gap-4">
        <span className="text-background/70">End</span>
        <span className="tabular-nums">
          {endPoint
            ? `${format(parseISO(endPoint.date), 'MMM d')}: ${String(endPoint.value)}`
            : 'N/A'}
        </span>
      </div>
    </div>
  );

  const triggerClassName = 'flex flex-row items-center cursor-help';
  const isUp = direction === 'up';
  const isDown = direction === 'down';
  const Icon = isUp ? ArrowUpIcon : isDown ? ArrowDownIcon : null;

  return (
    <Tooltip>
      <TooltipTrigger
        delay={CHANGE_TOOLTIP_DELAY}
        render={
          <div
            className={cn(
              triggerClassName,
              'text-sm/none font-medium',
              isUp && 'text-lime-600',
              isDown && 'text-red-600',
              direction === 'flat' && 'text-muted-foreground'
            )}
          />
        }
      >
        {Icon ? <Icon weight="bold" size={14} /> : null}
        <span>{absoluteChangePercent}%</span>
      </TooltipTrigger>
      <TooltipContent side="top" align="start">
        {content}
      </TooltipContent>
    </Tooltip>
  );
};

const ExtendedTrendArea = ({ data }: { data: ScoreTrendChartDatum[] }) => {
  const plotArea = usePlotArea();
  const xScale = useXAxisScale();
  const yScale = useYAxisScale();

  if (!plotArea || !xScale || !yScale || data.length === 0) {
    return null;
  }

  const points = data
    .map((datum) => {
      const x = xScale(datum.date, { position: 'middle' });
      const y = yScale(datum.trend);

      if (!isChartCoordinate(x) || !isChartCoordinate(y)) {
        return null;
      }

      return { x, y };
    })
    .filter((point): point is { x: number; y: number } => point !== null);

  if (points.length !== data.length) {
    return null;
  }

  if (points.length === 0) {
    return null;
  }

  const firstPoint = points[0];

  if (!firstPoint) {
    return null;
  }

  const lastPoint = points[points.length - 1] ?? firstPoint;

  const edgeOffset = SCORE_BAR_SIZE / 2;
  const startX = Math.max(plotArea.x, firstPoint.x - edgeOffset);
  const endX = Math.min(plotArea.x + plotArea.width, lastPoint.x + edgeOffset);

  return (
    <>
      <Curve
        type="monotone"
        layout="horizontal"
        points={[{ x: startX, y: firstPoint.y }, ...points, { x: endX, y: lastPoint.y }]}
        baseLine={plotArea.y + plotArea.height}
        stroke="none"
        fill="url(#fillTrend)"
        pointerEvents="none"
      />
      <Curve
        type="monotone"
        layout="horizontal"
        points={[{ x: startX, y: firstPoint.y }, ...points, { x: endX, y: lastPoint.y }]}
        stroke="var(--color-trend)"
        strokeWidth={1.5}
        fill="none"
        pointerEvents="none"
      />
    </>
  );
};

export const ScoreCard = ({ className, chartData, targetScore }: ScoreCardProps) => {
  const recentScores: number[] = [];
  let lastTrend: number | null = null;
  const trendChartData = chartData.map((datum) => {
    const score = clampScore(datum.score);

    if (score !== null) {
      recentScores.push(score);

      if (recentScores.length > TREND_WINDOW_SIZE) {
        recentScores.shift();
      }

      lastTrend = Math.round(
        recentScores.reduce((total, currentScore) => total + currentScore, 0) / recentScores.length
      );
    }

    return {
      ...datum,
      score,
      trend: lastTrend,
    };
  });
  const visibleChartData = trendChartData.slice(-MAX_VISIBLE_POINTS);
  const visibleComparisonPoints = visibleChartData.flatMap((datum) =>
    datum.trend !== null ? [{ date: datum.date, value: datum.trend }] : []
  );
  const startComparisonPoint = visibleComparisonPoints[0] ?? null;
  const endComparisonPoint = visibleComparisonPoints[visibleComparisonPoints.length - 1] ?? null;
  const currentScore =
    [...visibleChartData].reverse().find((datum) => datum.trend !== null)?.trend ?? 0;
  const changePercent =
    startComparisonPoint && endComparisonPoint
      ? Math.round(
          ((endComparisonPoint.value - startComparisonPoint.value) / startComparisonPoint.value) *
            100
        )
      : 0;

  return (
    <Card className={cn('flex-row py-3', className)}>
      <CardHeader className="shrink-0 py-1 h-full w-max @container-normal flex flex-col gap-0">
        <div className="flex items-center gap-2 -mt-1">
          <CardTitle className="text-foreground">Score</CardTitle>
          <CardActionMenu ariaLabel="Open score actions">
            <DropdownMenuGroup>
              <DropdownMenuItem>
                <PencilSimpleLineIcon weight="bold" />
                <span>Edit target</span>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <ChartLineUpIcon weight="bold" />
                <span>Go to analytics</span>
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </CardActionMenu>
        </div>
        <CardDescription className="mt-auto">
          <CardMetric>
            <CardMetricPrimary>
              <CardMetricValue size="lg">{currentScore}</CardMetricValue>
              <ScoreChangeIndicator
                changePercent={changePercent}
                startPoint={startComparisonPoint}
                endPoint={endComparisonPoint}
              />
            </CardMetricPrimary>
            <CardMetricSecondary className="uppercase tracking-wide">
              Target
              <CardMetricEmphasis className="ms-[0.5ch] tracking-tight tabular-nums">
                {targetScore}
              </CardMetricEmphasis>
            </CardMetricSecondary>
          </CardMetric>
        </CardDescription>
      </CardHeader>

      <CardContent className="min-w-0 flex-1 px-0">
        <ChartContainer
          config={chartConfig}
          className="h-full w-full select-none [&_g]:focus:outline-none"
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={visibleChartData}
              margin={{
                top: 0,
                right: CHART_RIGHT_MARGIN,
                left: 0,
                bottom: 0,
              }}
            >
              <defs>
                <linearGradient id="fillTrend" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--color-trend)" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="var(--color-trend)" stopOpacity={0} />
                </linearGradient>
              </defs>

              <XAxis dataKey="date" hide />
              <YAxis
                axisLine={false}
                tickLine={false}
                ticks={[SCORE_MIN, 505, SCORE_MAX]}
                interval={0}
                padding={{ top: 8, bottom: 8 }}
                tick={renderAxisTick}
                width={AXIS_GUTTER_WIDTH}
                orientation="right"
                tickMargin={0}
                domain={[SCORE_MIN, SCORE_MAX]}
                allowDataOverflow
              />
              <ReferenceLine y={targetScore} stroke="var(--color-gray-500)" strokeDasharray="4 4" />
              <ChartTooltip
                animationDuration={CHART_TOOLTIP_ANIMATION_DURATION}
                animationEasing="linear"
                filterNull={false}
                cursor={{ fill: 'rgba(21, 93, 252, 0.04)' }}
                content={
                  <ChartTooltipContent
                    formatter={(value, name) => (
                      <div className="flex flex-1 items-center justify-between leading-none">
                        <span className="text-muted-foreground">{getTooltipMetricLabel(name)}</span>
                        <span className="font-mono font-medium text-foreground tabular-nums">
                          {formatTooltipMetricValue(value)}
                        </span>
                      </div>
                    )}
                    labelFormatter={(_, payload) => {
                      const rawDate = (payload[0]?.payload as ScoreCardChartDatum | undefined)
                        ?.date;

                      return rawDate ? format(parseISO(rawDate), 'MMM d, yyyy') : null;
                    }}
                  />
                }
              />
              <ZIndexLayer zIndex={BADGE_Z_INDEX}>
                <ChartSideBadge
                  value={targetScore}
                  yValue={targetScore}
                  fill="#d9d2c5"
                  stroke="#c5bdaf"
                  textFill="#3f3a36"
                />
                {currentScore > 0 ? (
                  <ChartSideBadge
                    value={currentScore}
                    yValue={currentScore}
                    fill="#dbeafe"
                    stroke="#93c5fd"
                    textFill="#2563eb"
                  />
                ) : null}
              </ZIndexLayer>
              <ExtendedTrendArea data={visibleChartData} />
              <Bar
                dataKey="score"
                barSize={SCORE_BAR_SIZE}
                radius={[2, 2, 2, 2]}
                fill="var(--color-score)"
                fillOpacity={0.3}
              />
              <Area
                type="monotone"
                dataKey="trend"
                stroke="transparent"
                fill="transparent"
                dot={false}
                activeDot={{
                  r: 4,
                  fill: 'var(--color-trend)',
                  stroke: 'var(--color-background)',
                  strokeWidth: 1.5,
                }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};
